import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import PostAddIcon from '@mui/icons-material/PostAdd'
import MapsUgcIcon from '@mui/icons-material/MapsUgc'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import NotificationAddIcon from '@mui/icons-material/NotificationAdd'
import TimeAgo from 'components/utils/LocalizedTimeAgo'
import { MdOutlineEventNote } from "react-icons/md"
import { useTranslation } from 'react-i18next'

const getNotificationIcon = (topic) => {
  switch (topic) {
    case 'app.message.new':
      return <PostAddIcon />
    case 'marketplace.message.new':
      return <MapsUgcIcon />
    case 'app.request.new':
    case 'church.request.new':
      return <PersonAddAltIcon />
    case 'app.event.new':
    case 'app.event.discussion.comment.new':
      return <MdOutlineEventNote />
    case 'app.announcement.new':
    default:
      return <NotificationAddIcon />
  }
}

export default function NotifItem({ data }) {
  const navigate = useNavigate()
  const { t } = useTranslation('notifications')
  const preview = data?.preview || {}
  const isUnread = !data?.readAt
  const title = data?.title || t('newNotification')
  const subtitle = preview.subtitle || t('update')
  const senderName = data?.isAnonymous ? t('anonymous') : preview.title
  const createdAt = data?.createdAt ? new Date(data.createdAt) : new Date()

  const handleClick = () => {
    if(data?.deepLink){
      navigate(data.deepLink)
    }
  }

  const handleKeyDown = (e) => {
    if(e.key === 'Enter' || e.key === ' '){
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <NotifItemWrap
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      $unread={isUnread}
    >
      <MediaWrap>
        {preview.imageUrl ? (
          <img
            src={preview.imageUrl}
            alt=""
          />
        ) : (
          <NotifIconWrap>
            {getNotificationIcon(data?.topic)}
          </NotifIconWrap>
        )}
      </MediaWrap>

      <ContentWrap>
        <MetaRow>
          <TextCluster>
            <NotifItemHeader>
              <span>{subtitle}</span>
              {senderName && <strong>{senderName}</strong>}
            </NotifItemHeader>
            <NotificationTitle>{title}</NotificationTitle>
          </TextCluster>

          <TimeWrap>
            {isUnread && <UnreadDot aria-label={t('accessibility.unread')} />}
            <TimeStyle>
              <TimeAgo datetime={createdAt} />
            </TimeStyle>
          </TimeWrap>
        </MetaRow>
      </ContentWrap>
    </NotifItemWrap>
  )
}

const NotifItemWrap = styled.div`
  width: 100%;
  min-height: 86px;
  padding: 14px;
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  color: var(--lightThemeFontColor);
  cursor: pointer;
  border: none;
  border-radius: 25px;
  background: ${props => props.$unread ? 'rgba(0, 180, 204, 0.06)' : '#fff'};
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--themeColor);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 44px minmax(0, 1fr);
    padding: 12px;
  }
`

const MediaWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  background: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`

const NotifIconWrap = styled.div`
  width: 100%;
  height: 100%;
  color: var(--themeColor);
  background: rgba(0, 180, 204, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`

const ContentWrap = styled.div`
  min-width: 0;
`

const MetaRow = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-start;
  gap: 12px;
`

const TextCluster = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const NotifItemHeader = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: var(--dFontColor);

  span,
  strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-weight: 600;
    color: var(--lightThemeFontColor);
  }
`

const NotificationTitle = styled.div`
  min-width: 0;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.35;
  color: var(--lightThemeFontColor);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const TimeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
`

const TimeStyle = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 11px;
  color: var(--dFontColor);
`

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--themeColor);
  box-shadow: 0 0 0 3px rgba(0, 180, 204, 0.12);
`
